import { useState, useCallback } from 'react';
import { message } from 'antd';
import { n211Api, FN210, FN211 } from '../../../services/n211Api';
import { useAuth } from '../../../contexts/AuthContext';
import { useFieldErrors } from '../../../hooks/useFieldErrors';
import { Mode } from './useN211Mode';

export function useN211Record() {
  const { user } = useAuth();
  const [record, setRecord] = useState<FN210 | null>(null);
  const [formData, setFormData] = useState<Partial<FN210>>({});
  const [details, setDetails] = useState<Partial<FN211>[]>([]);
  const [selectedRow, setSelectedRow] = useState(0);
  const [loading, setLoading] = useState(false);
  const { setFieldErrors, clearFieldErrors, getFieldError, hasErrors } = useFieldErrors();

  /** Load a single record by ID */
  const loadRecord = useCallback(async (id: string): Promise<{
    data: FN210;
    dets: Partial<FN211>[];
  } | null> => {
    setLoading(true);
    try {
      const data = await n211Api.get(id);
      setRecord(data);
      setFormData({ ...data });
      const dets = data.details || [];
      setDetails(dets);
      setSelectedRow(0);
      return { data, dets };
    } catch {
      message.error('載入失敗');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /** Set a single master field */
  const setField = useCallback((key: keyof FN210, value: FN210[keyof FN210]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  /** Get a single master field value */
  const getField = useCallback((key: keyof FN210): FN210[keyof FN210] => {
    return (formData as Record<string, FN210[keyof FN210]>)[key] ?? '';
  }, [formData]);

  /** Get a detail field value from the selected row */
  const getDetailField = useCallback((key: keyof FN211): FN211[keyof FN211] => {
    const sel = details[selectedRow] as Partial<FN211> | undefined;
    return sel ? (sel as Record<string, FN211[keyof FN211]>)[key] ?? '' : '';
  }, [details, selectedRow]);

  /** Set a detail field value on the selected row */
  const setDetailField = useCallback((key: keyof FN211, value: FN211[keyof FN211]) => {
    setDetails(prev => {
      const next = [...prev];
      if (next[selectedRow]) {
        next[selectedRow] = { ...next[selectedRow], [key]: value };
      }
      return next;
    });
  }, [selectedRow]);

  /** Save (create or update) */
  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: FN210) => void,
  ) => {
    setLoading(true);
    try {
      const req = {
        master: formData as FN210,
        details: details as FN211[],
      };
      if (mode === 'add') {
        const result = await n211Api.create(req);
        message.success(`新增成功: ${result.fn210_01}`);
        setRecord(result);
        setFormData({ ...result });
        setDetails(result.details || []);
        onSuccess(result);
      } else {
        const result = await n211Api.update(formData.fn210_01!, req);
        message.success('修改成功');
        setRecord(result);
        setFormData({ ...result });
        setDetails(result.details || []);
        onSuccess(result);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { errors?: { field: string; message: string }[]; error?: string } } };
      const errors = err.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        setFieldErrors(errors);
        message.error('請修正標示的欄位');
      } else {
        message.error(err.response?.data?.error || '儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, details, setFieldErrors]);

  /** Delete the current record */
  const handleDelete = useCallback(async (): Promise<boolean> => {
    if (!record) return false;
    try {
      await n211Api.delete(record.fn210_01);
      message.success('刪除成功');
      return true;
    } catch {
      message.error('刪除失敗');
      return false;
    }
  }, [record]);

  /** Add a new detail row */
  const addDetailRow = useCallback(() => {
    const maxSeq = details.reduce((max, d) => Math.max(max, d.fn211_02 || 0), 0);
    if (maxSeq >= 99) {
      message.warning('最多 99 個明細項');
      return;
    }
    setDetails(prev => [...prev, { fn211_02: maxSeq + 1, fn211_01: formData.fn210_01 || '' }]);
  }, [details, formData.fn210_01]);

  /** Delete the currently selected detail row */
  const deleteDetailRow = useCallback(() => {
    if (details.length <= 1) return;
    setDetails(prev => {
      const next = prev.filter((_, i) => i !== selectedRow);
      return next.map((d, i) => ({ ...d, fn211_02: i + 1 }));
    });
    setSelectedRow(Math.max(0, selectedRow - 1));
  }, [details.length, selectedRow]);

  /** Reset form to current record (revert) */
  const revertToRecord = useCallback(() => {
    clearFieldErrors();
    if (record) {
      setFormData({ ...record });
      setDetails(record.details || []);
    }
  }, [record, clearFieldErrors]);

  /** Reset form for a new add */
  const resetForAdd = useCallback(() => {
    clearFieldErrors();
    setRecord(null);
    setFormData({
      fn210_13: 'NTD',
      fn210_14: 1,
      fn210_15: user?.userId || '',
      fn210_25: false,
      fn210_251: 1,
      fn210_80: 'N',
    });
    setDetails([{ fn211_02: 1 }]);
    setSelectedRow(0);
  }, [user, clearFieldErrors]);

  /** Apply result after save */
  const applyResult = useCallback((result: FN210) => {
    setRecord(result);
    setFormData({ ...result });
    setDetails(result.details || []);
  }, []);

  return {
    record, formData, details, selectedRow, loading, hasErrors,
    setSelectedRow, setDetails,
    loadRecord, setField, getField,
    getDetailField, setDetailField,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow,
    revertToRecord, resetForAdd, applyResult,
    getFieldError, clearFieldErrors,
  };
}
